import type {ReactElement} from 'react';
import {useContext, useRef, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import useOutsideClick from '@Hooks/useOutsideClick';
import {EventWizardDateTimeSummary} from '@Components/event-wizard/event-wizard-date-time-summary';
import {EventWizardDateTimeContent} from '@Components/event-wizard/event-wizard-date-time-content';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {openEventWizardDateTimeModal} from '@Modals/event-wizard-date-time-modal/event-wizard-date-time-modal.types';
import {isElementInClass} from '@Components/event-wizard/event-wizard.helper';
import {PreviewShortcutTarget} from '@Components/event-wizard/preview/preview.types';
import {usePreviewShortcutClicked} from '@Components/event-wizard/preview/use-preview-shortcut-clicked';
import styles from './event-wizard-date-time.module.scss';

export function EventWizardDateTime(): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const toggleExpandedState = (): void => {
    if (isSmallScreen) {
      openEventWizardDateTimeModal();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  /** Clicking the date in the preview acts like clicking this card, and brings it into view. */
  usePreviewShortcutClicked(PreviewShortcutTarget.DATE_TIME, (): void => {
    toggleExpandedState();
    containerRef.current?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
  });

  const toggleContainerExpand = (e: MouseEvent | PointerEvent): void => {
    const el = e.target as HTMLElement;

    if (!isElementInClass(el, 'date-time-content-container')) {
      setIsExpanded(false);
    }
  };
  // Dismiss on click, not mousedown: collapsing this section pulls the tiles below it upwards, and on mousedown that
  // happens while the button is still held — so the control being pressed moves out from under the cursor and never
  // receives its click.
  useOutsideClick(containerRef, toggleContainerExpand, null, null, 'click');

  return (
    <div ref={containerRef}>
      <div className="flex-row-align-center spacing-m-b-1">
        <Text val={window.i18next.t('pmwjs_date_time')} size={TextSize.XXSMALL} bold />
      </div>
      <EventWizardDateTimeSummary toggleExpandedState={toggleExpandedState} isExpanded={isExpanded} />
      {isSmallScreen ? undefined : (
        <div className={`date-time-content-container spacing-p-3 border-s-primary radius-8 ${styles.fadeIn} ${!isExpanded ? '_hidden' : ''}`}>
          <EventWizardDateTimeContent />
        </div>
      )}
    </div>
  );
}
